import { useMemo } from 'react';

import { FullScreenLoading } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { getAssessmentTenure } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/assessMyTrack.utils';
import { useAssessmentBreakdown } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/hooks/useAssessmentBreakdown';
import { AssessmentBreakdown } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/molecules/AssessmentBreakdown';
import { CareerTrackTimelineChart } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/molecules/CareerTrackTimelineChart';
import { ChecklistEditModal } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/molecules/ChecklistEditModal';
import { DimensionEditModal } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/molecules/DimensionEditModal';
import { useGetMemberTrackAssessmentQuery } from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import { useGetAcademicGradesQuery } from '@backrs_ui/grow-ui/src/redux/trackAssessment/trackAssessment.api';

import { Box, type SxProps, type Theme } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { useAgentContext } from './MemberAyotView.agentContext';
import { MemberTabErrorState, TabErrorMessage } from './MemberTabErrorState';
import { useCurrentMember } from './useCurrentMember';

const {
  modules: { coachingDashboard },
} = appStrings;

export const MemberAyotView = () => {
  const currentMember = useCurrentMember();
  const { member, isLoading: isLoadingMember, isError: isMemberError } = currentMember;

  const {
    data,
    isLoading: isLoadingAssessment,
    isError,
  } = useGetMemberTrackAssessmentQuery(member ? member.id : skipToken);

  const { data: academicGrades } = useGetAcademicGradesQuery(undefined);

  const minimumTrack = data?.minimumTrack ?? undefined;
  const assessmentBreakdown = useAssessmentBreakdown({ minimumTrack });

  const { getBreakdownForAssessment, makeCriteriaDescriptionGetter } = assessmentBreakdown;
  const result = data?.result;

  const breakdown = useMemo(
    () => getBreakdownForAssessment(result),
    [getBreakdownForAssessment, result],
  );

  const tenure = getAssessmentTenure(result);

  const getCriteriaDescription = useMemo(
    () =>
      makeCriteriaDescriptionGetter(
        result?.academic_grade_id,
        false,
        result?.careerTrack?.id,
        tenure,
      ),
    [makeCriteriaDescriptionGetter, result?.academic_grade_id, result?.careerTrack?.id, tenure],
  );

  const { editingDimension } = assessmentBreakdown;

  const criteriaDescriptions = editingDimension
    ? {
        advanced: getCriteriaDescription(
          editingDimension.categorySlug,
          editingDimension.dimensionName,
          'Advanced',
        ),
        minimum: getCriteriaDescription(
          editingDimension.categorySlug,
          editingDimension.dimensionName,
          'Minimum',
        ),
      }
    : undefined;

  useAgentContext(member);

  if (isMemberError) return <MemberTabErrorState {...currentMember} />;

  if (isLoadingMember || isLoadingAssessment) {
    return <FullScreenLoading fillContainer />;
  }

  if (!member) return null;

  if (isError || !result) {
    return <TabErrorMessage message={coachingDashboard.trackAssessmentError} />;
  }

  const gradeName = academicGrades?.find((grade) => grade.id === result.academic_grade_id)?.name;

  return (
    <Box sx={containerSx}>
      <CareerTrackTimelineChart
        minimumTrack={minimumTrack}
        trackAssessmentResult={result}
        subject={{
          name: `${member.first_name} ${member.last_name}`,
          history: data.history,
          profileImageUrl: member.profile_image,
        }}
        includeBlueprintText
        showLastAssessed
      />
      {breakdown && gradeName && (
        <AssessmentBreakdown
          breakdown={breakdown}
          gradeName={gradeName}
          tenure={tenure}
          academicGradeId={result.academic_grade_id}
          onDimensionClick={assessmentBreakdown.handleDimensionClick}
          isReadOnly={assessmentBreakdown.isReadOnly}
        />
      )}

      <DimensionEditModal
        key={editingDimension?.scoreId}
        open={!!editingDimension}
        onClose={assessmentBreakdown.clearEditingDimension}
        dimension={editingDimension}
        onSubmit={assessmentBreakdown.handleDimensionSubmit}
        criteriaDescriptions={criteriaDescriptions}
        isReadOnly={assessmentBreakdown.isReadOnly}
      />

      {assessmentBreakdown.editingProfileField && (
        <ChecklistEditModal
          open
          onClose={() => assessmentBreakdown.setEditingProfileField(null)}
          fieldKey={assessmentBreakdown.editingProfileField.fieldKey}
          value={assessmentBreakdown.editingProfileField.value}
          isReadOnly={assessmentBreakdown.isReadOnly}
        />
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  // The chart and breakdown size themselves with container queries, so they need a container here.
  containerType: 'inline-size',
  paddingRight: backrs.spacing['spacing-md'],
});
